import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, ExternalLink, Send, Eye, Ban, Gift, Loader2, RotateCcw, Upload, Banknote, Split } from "lucide-react";
import { api, argent } from "./api";
import { useConfirmation } from "./Confirmation";
import { GRAVITES, Section, Eyebrow, Case, BarreSelection, BoutonSombre } from "./ui";
import { ModaleEchelonnement } from "./Facturation";
import { apercuFusion, useChoixFusion } from "./FusionFiches";

import { DecisionParrainage, DecisionsPrises, SaisieAvs, AttributionAvs, ApercuEmail, DecisionDesinscription } from "./NotifDecisions";
import ImportReleve from "./Encaissements";

const ACTIONS = {
  relancer: { label: "Relancer", Icone: Send, lot: "Relancer la sélection" },
  // Le libellé du lot dit le nombre de familles réellement choisies (cf. `libelleLot`).
  demander_avs: { label: "Demander par e-mail", Icone: Send, lot: "Demander par e-mail" },
  echelonner: { label: "Échelonner le paiement", Icone: Split, lot: null, parLigne: true },
  // « Annuler la sélection » se lisait « décocher » ou « annuler les doublons » (école,
  // 09.10.2026) : le bouton dit ce qu'il fait, la ligne EN TROP passe « Annulé ».
  annuler_inscription: { label: "Annuler la ligne en double", Icone: Ban, lot: "Annuler les lignes en double" },
  vu: { label: "Vu, c'est normal", Icone: Eye, lot: "Marquer la sélection comme vue" },
  // Adresses e-mail rejetées (02.11.2026) : la ligne part d'elle-même quand l'adresse est
  // corrigée ; « Réglé » la range à la main (adresse réglée autrement, par téléphone…).
  regle: { label: "Réglé", Icone: Check, lot: "Marquer la sélection comme réglée" },
  // Un résumé de ce qui est déjà parti : on le range, il n'y a rien à décider.
  vu_resume: { label: "Marquer comme vu", Icone: Eye, lot: "Marquer la sélection comme vue" },
  // Les nouvelles inscriptions ne se « valident » pas : on les lit.
  lu: { label: "Marquer comme lu", Icone: Eye, lot: "Marquer comme lu" },
  donner_place: { label: "Donner la place", Icone: Check, lot: "Donner la place à la sélection" },
  fusionner_fiches: { label: "Fusionner les fiches", Icone: Check,
    lot: "Fusionner la sélection" },
  confirmer_place_spectacle: { label: "Confirmer et prévenir", Icone: Check,
    lot: "Confirmer la sélection et prévenir les familles" },
  aller_facturation: { label: "Voir dans Facturation", Icone: ExternalLink, lot: null },
  // « Cours sans prix » / « Cours sans tarif » : une erreur à corriger, pas à « voir ». Pas
  // de « Vu, c'est normal » : seul « Ouvrir » (la fiche, le cours), la ligne part une fois
  // le prix connu (école, 09.10.2026).
  a_corriger: { label: "Ouvrir", Icone: ExternalLink, lot: null },
  retirer_taxe: { label: "Retirer la taxe", Icone: Ban, lot: "Retirer la taxe de la sélection" },
  importer_releve: { label: "Importer le relevé", Icone: Upload, lot: "Importer le relevé bancaire" },
  confirmer_especes: { label: "Payé en espèces", Icone: Banknote, lot: "Passer la sélection en payée" },
  repondre: { label: "Répondre", Icone: ExternalLink, lot: null },
  renvoyer_attestation: { label: "Envoyer la nouvelle attestation", Icone: Send,
    lot: "Envoyer les nouvelles attestations de la sélection" },
  envoyer_attestation: { label: "Envoyer l'attestation", Icone: Send,
    lot: "Envoyer les attestations de la sélection" },
};

const CLE_MASQUES_OUVERTS = "console-atraiter-masques-ouverts";
/* Familles par envoi de « Demander par e-mail » (PAQUET_AVS de notifications.py). */
const PAQUET_AVS = 25;
const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? "s" : ""}`;

/* Le bouton de la barre de sélection dit ce qu'il va faire, chiffres compris.
   « Demander par e-mail (25 familles à la fois) » avec UNE famille cochée : « ça veut
   dire quoi ? » (école, 09.10.2026). Un seul e-mail par famille : on compte les familles. */
const familles = (selection, items) => new Set(selection.map((ref) => {
  const it = items.find((i) => i.ref === ref);
  return (it && it.famille) || ref;
})).size;
const libelleLot = (g, action, selection, items) => {
  if (g.type_action === "demander_avs") return `${action.lot} (${pluriel(familles(selection, items), "famille")})`;
  if (g.type_action === "annuler_inscription") {
    return selection.length > 1 ? `${action.lot} (${selection.length})` : "Annuler la ligne en double (1)";
  }
  return action.lot;
};

export default function Notifications({ data, onRecharger, onAller, cible }) {
  const { informer, demander } = useConfirmation();
  const { choisir, fenetre: fenetreFusion } = useChoixFusion();
  // `cible` : un groupe (« Voir dans À traiter » d'une alerte à l'école, backend
  // mail.lien_console) — il s'ouvre déplié et vient à l'écran dès que la liste est là.
  const [ouverts, setOuverts] = useState(() => (cible ? { [cible]: true } : {}));
  const amene = useRef(false);
  useEffect(() => {
    if (!cible || !data || amene.current) return undefined;
    // Un court délai : les encadrés du haut (relevé, parrainages) arrivent juste après.
    const t = setTimeout(() => {
      const el = [...document.querySelectorAll("[data-testid^='notif-groupe-']")]
        .find((x) => x.getAttribute("data-testid") === `notif-groupe-${cible}`);
      // La marge garde le titre du groupe sous le bandeau collant de la console.
      if (el) { amene.current = true; el.style.scrollMarginTop = "96px"; el.scrollIntoView({ block: "start" }); }
    }, 500);
    return () => clearTimeout(t);
  }, [cible, data]);
  const [choisis, setChoisis] = useState({});
  const [enCours, setEnCours] = useState("");
  /* Un envoi en lot (rappels, attestations) prend plusieurs secondes par famille : sans
     compteur, l'école ne sait pas si quelque chose se passe et reclique. */
  const [progres, setProgres] = useState(null);
  const [parrainages, setParrainages] = useState(null);
  const [releve, setReleve] = useState(null);
  const [demandeReleve, setDemandeReleve] = useState(null);
  /* Paiement partiel : l'école formalise le reste en versements depuis cet écran. */
  const [echelon, setEchelon] = useState(null);
  /* « Lignes masquées » : repliée tant que la personne ne l'a pas ouverte. Le choix reste
     sur CE navigateur (stockage local, qui peut manquer : navigation privée, etc.). */
  const [masquesOuverts, setMasquesOuverts] = useState(() => {
    try { return window.localStorage.getItem(CLE_MASQUES_OUVERTS) === "oui"; } catch { return false; }
  });
  const basculerMasques = (ouvrir) => {
    setMasquesOuverts(ouvrir);
    try { window.localStorage.setItem(CLE_MASQUES_OUVERTS, ouvrir ? "oui" : "non"); } catch { /* sans gravité */ }
  };

  const chargerReleve = async () => {
    const d = await api("/releves/dernier");
    setReleve(d);
    return d;
  };
  useEffect(() => { chargerReleve().catch(() => {}); }, []);

  useEffect(() => { api("/parrainages").then(setParrainages).catch(() => {}); }, [data]);

  if (!data) return <p className="text-[var(--muted)]">Chargement…</p>;

  // Le message est classé sans réponse : il sort de « À traiter » et passe dans Messages → Traités.
  const marquerMessagesTraites = async (ids) => {
    setEnCours("messages");
    try {
      await api("/messages/lot", { method: "POST", body: JSON.stringify({ ids, action: "traite", sources: {} }) });
      setChoisis({ ...choisis, messages: [] });
      await onRecharger();
    } catch (e) {
      await informer({ titre: "Action interrompue", ton: "danger", texte: e.message });
    }
    setEnCours("");
  };

  // « Revoir ce qui a été masqué » : les lignes rangées par « Vu, c'est normal » reviennent.
  const reafficher = async (gid) => {
    if (!await demander({
      titre: "Revoir ce qui a été masqué",
      texte: "Les lignes marquées « vu » dans ce groupe réapparaissent, pour revenir sur un clic trop rapide.",
      points: ["Rien n'est effacé ni modifié : c'est seulement l'affichage."],
      valider: "Tout réafficher",
    })) return;
    await api(`/notifications/${gid}/reafficher`, { method: "POST" });
    onRecharger();
  };

  /* `forcer` : « Relancer quand même sans charger de relevé », le même chemin que
     Facturation (le serveur reçoit `forcer: true` et renvoie l'avertissement du relevé). */
  const executer = async (groupe, type, refs, items, forcer = false) => {    if (type === "importer_releve") { setDemandeReleve({ refs: [], items: [] }); return; }
    // Formaliser le reste d'un paiement partiel en versements, sans quitter « À traiter ».
    if (type === "echelonner") {
      const it = (items || []).find((i) => i.ref === refs[0]);
      const e = it?.echelonnement;
      if (e) setEchelon({
        client: e.client, total: e.total, recu: e.recu,
        echelonnement_nb: e.nb, echelonnement_dates: e.dates,
        postes: (e.ids || []).map((id) => ({ id })),
      });
      return;
    }
    setEnCours(groupe);
    try {
      if (type === "relancer") {
        const etatReleve = await chargerReleve();
        // Relevé manquant ou trop vieux : on propose de l'importer d'abord (pour ne pas
        // relancer une famille qui a payé), mais la fenêtre offre aussi « Relancer quand
        // même » — sans cela, un relevé jamais importé (compte de Noémie) bloquait tout.
        if (!etatReleve.valide && !forcer) { setDemandeReleve({ groupe, refs, items }); setEnCours(""); return; }
        const envois = [], ignorees = [];
        let reel = true, avertissement = "";
        setProgres({ faits: 0, total: refs.length, quoi: "rappel(s)" });
        for (let i = 0; i < refs.length; i += 5) {
          const paquet = await api("/notifications/impayes/relancer",
            { method: "POST", body: JSON.stringify({ refs: refs.slice(i, i + 5), ...(forcer ? { forcer: true } : {}) }) });
          envois.push(...paquet.envois);
          ignorees.push(...(paquet.ignorees || []));
          reel = paquet.mode_reel;
          avertissement = paquet.avertissement || avertissement;
          setProgres({ faits: Math.min(i + 5, refs.length), total: refs.length, quoi: "rappel(s)" });
        }
        setProgres(null);
        const r = { envois, ignorees, mode_reel: reel };
        /* Le serveur revérifie chaque famille (la même règle que Facturation) : une famille
           devenue à jour entre-temps est écartée, et on dit combien, et pourquoi. */
        const parRaison = {};
        ignorees.forEach((i) => { const g = i.groupe || i.motif; parRaison[g] = (parRaison[g] || 0) + 1; });
        await informer({
          titre: `${r.envois.length} rappel(s) envoyé(s)`, ton: avertissement ? "attention" : "succes",
          texte: r.mode_reel ? "Les familles concernées ont reçu leur rappel."
            : "MODE TEST : aucun e-mail n'est parti, ni aux familles ni ailleurs.",
          points: [...(avertissement ? [`Relevé bancaire : ${avertissement}`] : []),
            ...(ignorees.length ? [`${ignorees.length} ignorée(s) : ${Object.entries(parRaison)
              .map(([g, n]) => `${n} ${g}`).join(", ")}`] : []),
            ...(r.ignorees || []).map((i) => `${i.client || i.ref} — ${i.motif}`)],
        });
      } else if (type === "confirmer_especes") {
        // L'argent a été encaissé au comptoir : la facture passe en payée, on ne la
        // « marque » pas simplement comme vue.
        // Les cartes 10 cours et les workshops (« carte10::… », « workshop::… ») passent
        // « payés » comme dans Facturation › Cash / espèces ; les factures, en payées.
        const r = await api("/facturation/especes/confirmer", { method: "POST", body: JSON.stringify({ ids: refs }) });
        const horsFacture = refs.some((ref) => /^(carte10|workshop)::/.test(ref));
        await informer({
          titre: `${r.confirmes.length} paiement(s) confirmé(s)`, ton: "succes",
          texte: `${argent(r.total)} encaissés au comptoir${horsFacture ? " (espèces ou TWINT)" : " en espèces"}.`
            + (horsFacture ? "\nCartes 10 cours et workshops : « payé » dans Facturation › Cash / espèces." : ""),
        });
      } else if (type === "retirer_taxe") {
        const r = await api("/notifications/taxe-admin/retirer", { method: "POST", body: JSON.stringify({ refs }) });
        await informer({
          titre: `${r.retirees.length} taxe(s) retirée(s)`, ton: r.a_crediter.length ? "attention" : "succes",
          texte: "Les totaux concernés ont été refaits.",
          points: r.a_crediter.length
            ? [`${r.a_crediter.length} facture(s) déjà envoyée(s) : à créditer à la main.`] : [],
        });
      } else if (type === "renvoyer_attestation" || type === "envoyer_attestation") {
        // L'attestation reprend les montants de la facture : on la refait telle quelle et
        // on la renvoie. L'envoi part en tâche de fond, on suit le lot seconde par seconde.
        const { lot, total } = await api("/attestations/envoyer", {
          method: "POST", body: JSON.stringify({ refs }),
        });
        setProgres({ faits: 0, total, quoi: "attestation(s)" });
        let etape = { envois: [], echecs: [], mode_reel: true };
        for (;;) {
          await new Promise((r) => setTimeout(r, 1000));
          etape = await api(`/attestations/envoi/${lot}`);
          if (!etape.suivi_perdu) {
            setProgres({ faits: etape.faits, total: etape.total, quoi: "attestation(s)" });
          }
          if (etape.fini) break;
        }
        setProgres(null);
        if (etape.suivi_perdu || etape.interrompu) {
          await informer({
            titre: etape.interrompu
              ? `${etape.envois.length} attestation(s) envoyée(s) — suivi interrompu`
              : "Envoi terminé, suivi perdu",
            ton: "attention",
            texte: "Le serveur a perdu le fil de la progression (redémarrage pendant l'envoi). "
              + "Les attestations parties sont marquées « envoyée ».",
            points: ["Les lignes encore présentes ici ne sont pas parties : relance-les."],
          });
        } else {
          await informer({
            titre: `${etape.envois.length} nouvelle(s) attestation(s) envoyée(s)`,
            ton: etape.echecs.length ? "attention" : "succes",
            texte: etape.mode_reel
              ? "Les familles ont reçu la version à jour, avec les montants de la facture corrigée."
              : "MODE TEST : rien n'est parti aux familles.",
            points: etape.echecs.map((e) => `${String(e.ref).split("::").pop()} — ${e.motif}`),
          });
        }
      } else if (type === "demander_avs") {
        const r = await api("/notifications/avs/demander", { method: "POST", body: JSON.stringify({ refs }) });
        await informer({
          titre: `${r.envois.length} e-mail(s) envoyé(s)`, ton: "succes",
          texte: (r.mode_reel ? "Un seul e-mail par famille." : "MODE TEST : rien n'est parti aux familles.")
            + (r.reste ? `\n${r.reste} famille(s) restante(s) : relance le bouton pour le paquet suivant.` : ""),
        });
      } else if (type === "donner_place") {
        // La place se libère : l'inscription passe de « Liste d'attente » à « Inscrit »,
        // et la facturation de la famille est refaite dans la foulée.
        const lot = refs.map((ref) => items.find((i) => i.ref === ref)).filter(Boolean);
        const sansPlace = lot.filter((i) => !i.libres);
        if (sansPlace.length && !await demander({
          titre: `${sansPlace.length} cours encore complet(s)`,
          ton: "attention",
          texte: "Aucune place ne s'est libérée pour cette ou ces inscriptions.",
          points: ["Tu peux donner la place quand même (dépassement de la capacité, à assumer côté salle).",
            "Sinon, ouvre la fiche de l'élève et propose-lui un autre cours avec « Changer de cours »."],
          valider: "Donner la place quand même",
        })) { setEnCours(""); return; }
        // La famille est prévenue par e-mail (« Une place s'est libérée »). « Quand même »
        // n'est envoyé que pour les cours que l'école a vus complets et confirmés : une
        // place libre déjà donnée entre-temps (double clic, deux personnes) est refusée.
        let reel = true;
        const sansEmail = [];
        const refusees = [];
        for (const it of lot) {
          try {
            const r = await api(`/inscriptions/${it.inscription_id}/donner-place`, {
              method: "POST", body: JSON.stringify({ quand_meme: sansPlace.includes(it) }),
            });
            reel = r.mode_reel;
            if (r.sans_email) sansEmail.push(String(it.titre || it.inscription_id).split(" · ")[0]);
          } catch (e) {
            refusees.push(`${String(it.titre || it.inscription_id).split(" · ")[0]} — ${e.message}`);
          }
        }
        const donnees = lot.length - refusees.length;
        await informer({
          titre: `${donnees} place(s) attribuée(s)`,
          ton: refusees.length || sansEmail.length ? "attention" : "succes",
          texte: (donnees ? "Les inscriptions passent en « Inscrit » : la facture de la famille est refaite et apparaît dans « À générer ».\n"
            + (reel ? "Chaque famille a reçu l'e-mail « Une place s'est libérée »."
              : "MODE TEST : rien n'est parti aux familles.") : "Aucune place n'a été donnée."),
          points: [...sansEmail.map((t) => `${t} — pas d'adresse e-mail : à prévenir par téléphone`),
            ...refusees],
        });
      } else if (type === "fusionner_fiches") {
        // Les deux fiches n'en font plus qu'une : la fiche qui porte les cours garde la
        // main et récupère ce qui lui manquait (AVS, téléphone, naissance). L'aperçu de
        // chaque paire (rien n'est écrit) dit d'abord si les fiches se contredisent (nom,
        // date de naissance, AVS…) : pour celles-là, une fenêtre par enfant demande
        // quoi garder ; les autres se fusionnent comme avant, après une seule question.
        const apercus = [];
        const refuses = [];
        for (const ref of refs) {
          try {
            apercus.push({ ref, apercu: await apercuFusion({ eleve_id: ref }) });
          } catch (e) {
            refuses.push(`${items.find((i) => i.ref === ref)?.titre || ref} : ${e.message}`);
          }
        }
        const aChoisir = apercus.filter((a) => a.apercu.conflits?.length);
        if (refs.length > 1 || !aChoisir.length) {
          if (!apercus.length) {
            await informer({ titre: "Fusion impossible", ton: "danger", texte: refuses.join("\n") });
            setEnCours("");
            return;
          }
          if (!await demander({
            titre: apercus.length > 1 ? `Fusionner ${apercus.length} enfants en double` : "Fusionner ces deux fiches",
            texte: "Les fiches du même enfant sont réunies en une seule.",
            points: ["La fiche qui porte les cours est conservée et complétée avec les informations de l'autre.",
              "Cours, essais, stages et parrainages sont déplacés dessus.",
              "L'autre fiche est archivée : rien n'est effacé, elle revient d'un clic.",
              ...(aChoisir.length ? [`${aChoisir.length} enfant(s) dont les deux fiches ne disent pas la même chose (nom, date de naissance, AVS…) : je te demande quoi garder, une fenêtre par enfant.`] : []),
              ...refuses.map((t) => `Pas fusionnable : ${t}`)],
            valider: "Fusionner",
          })) { setEnCours(""); return; }
        }
        const messages = [];
        const passes = [];
        let etape = 0;
        for (const { ref, apercu } of apercus) {
          let choix = null;
          if (apercu.conflits?.length) {
            etape += 1;
            choix = await choisir({
              apercu,
              titre: `Fusionner les deux fiches de ${apercu.garde.prenom} ${apercu.garde.nom}`,
              sous_titre: aChoisir.length > 1 ? `Enfant ${etape} sur ${aChoisir.length} dont les fiches se contredisent` : "",
              annuler: aChoisir.length > 1 || apercus.length > 1 ? "Passer cet enfant" : "Annuler",
            });
            if (!choix) { passes.push(`${apercu.garde.prenom} ${apercu.garde.nom}`.trim()); continue; }
          }
          const corps = { eleve_id: ref, ...(apercu.absorbe ? { autre_id: apercu.absorbe.id } : {}),
            ...(choix ? { choix } : {}) };
          let r;
          try {
            r = await api("/eleves/doublons/fusionner-paire", { method: "POST", body: JSON.stringify(corps) });
          } catch (e) {
            // Deux familles différentes (parents séparés, cas Sculfort) : la fusion relie
            // aussi leurs factures, elle n'a lieu qu'après une confirmation explicite.
            if (!/deux familles différentes/.test(e.message || "")) {
              refuses.push(`${apercu.garde.prenom} ${apercu.garde.nom} : ${e.message}`);
              continue;
            }
            if (!await demander({
              titre: "Deux familles différentes",
              texte: e.message,
              points: ["Fusionner relie aussi les deux adresses : une seule facture pour la famille.",
                "À faire seulement si c'est bien le même enfant (parents séparés, par exemple)."],
              valider: "Fusionner quand même",
            })) { passes.push(`${apercu.garde.prenom} ${apercu.garde.nom}`.trim()); continue; }
            r = await api("/eleves/doublons/fusionner-paire", {
              method: "POST", body: JSON.stringify({ ...corps, confirmer: true }),
            });
          }
          messages.push(r.message);
        }
        if (!messages.length && !refuses.length && passes.length === apercus.length && refs.length === 1) {
          setEnCours("");
          return;
        }
        await informer({
          titre: messages.length ? `${messages.length} enfant(s) sans doublon` : "Rien n'a été fusionné",
          ton: refuses.length || !messages.length ? "attention" : "succes",
          texte: messages.length ? "Les fiches sont réunies." : "Aucune fiche n'a été fusionnée.",
          points: [...messages.slice(0, 5),
            ...passes.map((n) => `${n} : passé, les deux fiches restent dans la liste.`),
            ...refuses.map((t) => `Pas fusionné — ${t}`)],
        });
      } else if (type === "confirmer_place_spectacle") {
        // La place de spectacle se donne dans l'ordre d'arrivée. La famille reçoit un
        // e-mail : rien ne part avant ce clic.
        const lot = refs.map((ref) => items.find((i) => i.ref === ref)).filter(Boolean);
        const trop = lot.filter((i) => !i.rentre);
        if (trop.length && !await demander({
          titre: `${trop.length} réservation(s) ne rentre(nt) pas dans la salle`,
          ton: "attention",
          texte: "Il n'y a pas assez de places libres pour cette ou ces réservations.",
          points: ["Tu peux confirmer quand même (la salle sera au-delà de sa jauge, à assumer côté sécurité).",
            "Sinon, attends une annulation : la console te le proposera toute seule."],
          valider: "Confirmer quand même",
        })) { setEnCours(""); return; }
        let reel = false;
        for (const it of lot) {
          const r = await api(`/spectacles/reservations/${it.reservation_id}/confirmer`, { method: "POST" });
          reel = reel || r.mode_reel;
        }
        await informer({
          titre: `${lot.length} place(s) confirmée(s)`, ton: "succes",
          texte: reel
            ? "Chaque famille a reçu l'e-mail « une place s'est libérée »."
            : "MODE TEST : les e-mails ne sont pas partis aux familles.",
        });
      } else if (type === "annuler_inscription") {
        // Dire AVANT ce que fait le bouton (« ça veut dire quoi ? que ça annule les
        // doublons ? », école, 09.10.2026) : pour chaque élève, la ligne EN TROP passe
        // « Annulé », l'autre ne bouge pas. L'aperçu du serveur (rien n'est écrit) dit si
        // une facture est déjà partie pour ces lignes.
        const lot = refs.map((ref) => items.find((i) => i.ref === ref)).filter(Boolean);
        const apercus = await Promise.all(lot.map((it) => api(
          `/inscriptions/${encodeURIComponent(it.inscription_id)}/impact?statut=${encodeURIComponent("Annulé")}`,
        ).catch(() => null)));
        const parties = apercus.filter((d) => (d?.changements || []).some((c) => c.recalcul_requis)).length;
        const facture = apercus.some((d) => !d)
          ? "Facture : si une facture déjà envoyée comptait le cours deux fois, elle passe dans « À corriger » ; rien ne repart sans ta validation."
          : parties
            ? `Facture : ${parties} ligne(s) sur une facture déjà envoyée. Si son montant change (cours compté deux fois), elle passe dans « À corriger » et rien ne repart sans ta validation ; sinon elle ne bouge pas.`
            : "Facture : aucune facture déjà envoyée n'est touchée ; la ligne en trop ne compte simplement plus dans le prochain calcul.";
        const montres = lot.slice(0, 6);
        if (!await demander({
          titre: lot.length > 1 ? `Annuler ${lot.length} lignes en double` : "Annuler la ligne en double",
          texte: "Chaque élève reste inscrit au cours : seule la ligne EN TROP passe en « Annulé ». "
            + "La ligne gardée ne change pas.",
          points: [
            ...montres.map((it) => `${it.titre}${it.cours ? ` — ${it.cours}` : ""} : annulée, la ligne `
              + `${it.annulee || "en trop"} · gardée, la ligne ${it.gardee || "d'origine"}.`),
            ...(lot.length > montres.length ? [`… et ${lot.length - montres.length} autre(s), même règle.`] : []),
            "Rien n'est effacé : la ligne annulée reste sur la fiche de l'élève et dans son historique.",
            facture,
            "La place prise en double est libérée dans le cours. Aucun e-mail ne part à la famille.",
          ],
          valider: lot.length > 1 ? `Annuler les ${lot.length} lignes en double` : "Annuler la ligne en double",
          annuler: "Ne rien changer",
        })) { setEnCours(""); return; }
        for (const it of lot) {
          // La note du doublon s'ajoute aux notes de la ligne, elle ne les remplace plus.
          const notes = [it.notes, "Doublon annulé depuis la console"].filter(Boolean).join(" · ");
          await api(`/inscriptions/${encodeURIComponent(it.inscription_id)}`, {
            method: "PATCH",
            body: JSON.stringify({ champs: { statut: "Annulé", notes_internes: notes } }),
          });
        }
      } else {
        // Une seule requête pour toute la sélection : le marquage ligne par ligne
        // faisait 274 allers-retours en série sur les grosses listes.
        await api(`/notifications/${groupe}/traite-lot`, {
          method: "POST",
          body: JSON.stringify({ refs }),
        });
      }
      setChoisis({ ...choisis, [groupe]: [] });
      await onRecharger();
    } catch (e) {
      await informer({ titre: "Action interrompue", ton: "danger", texte: e.message });
    }
    setEnCours("");
  };

  return (
    <div className="space-y-8" data-testid="console-notifications">
      {fenetreFusion}
      <div className="flex flex-wrap items-start gap-4">
        <div>
          <Eyebrow>À traiter</Eyebrow>
          <h1 className="font-display text-3xl tracking-tight mt-1">
            {data.total === 0 ? "Rien à traiter" : `${data.total} action(s) en attente`}
          </h1>
          <p className="text-sm text-[var(--muted)] mt-1 max-w-3xl">
            Seul ce qui demande une décision ou un geste de l'école apparaît ici. Chaque groupe
            porte son action, exécutable ligne par ligne ou sur une sélection.
            {!data.mode_reel_emails && (
              <span className="block mt-1 font-semibold" style={{ color: "#B45309" }}>
                MODE TEST : aucun e-mail ne part, ni aux familles ni sur une adresse de test.
              </span>
            )}
          </p>
        </div>
      </div>

      {progres && (
        <div className="bg-white rounded-2xl border p-4" style={{ borderColor: "var(--border)" }}
          data-testid="notif-progression">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold">Envoi en cours…</span>
            <span className="text-[var(--muted)]">
              {progres.faits} / {progres.total} {progres.quoi}
            </span>
          </div>
          <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "#EEF2F6" }}>
            <div className="h-full transition-all"
              style={{ width: `${Math.round((progres.faits / Math.max(progres.total, 1)) * 100)}%`,
                       background: "var(--primary)" }} />
          </div>
          <p className="text-xs text-[var(--muted)] mt-2">
            Laisse cet écran ouvert : chaque envoi part l'un après l'autre, sans doublon.
          </p>
        </div>
      )}

      {demandeReleve && (
        <ImportReleve
          releve={releve}
          onFermer={() => setDemandeReleve(null)}
          /* Même issue que Facturation : relancer sans relevé, en connaissance de cause. */
          actionSecondaire={demandeReleve.refs?.length ? {
            label: "Relancer quand même sans charger de relevé",
            aide: "À utiliser seulement si tu sais déjà qui a payé : sinon une famille à jour recevra un rappel.",
            onClick: async () => {
              const d = demandeReleve;
              setDemandeReleve(null);
              await executer(d.groupe || "impayes", "relancer", d.refs, d.items, true);
            },
          } : null}
          onTermine={async () => {
            const r = await chargerReleve();
            const d = demandeReleve;
            setDemandeReleve(null);
            await onRecharger();
            if (r.valide && d?.refs?.length) await executer(d.groupe || "impayes", "relancer", d.refs, d.items);
          }}
        />
      )}

      {echelon && (
        <ModaleEchelonnement facture={echelon} onFermer={() => setEchelon(null)}
          onOk={async () => { setEchelon(null); await onRecharger(); }} />
      )}

      {parrainages && (
        <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="carte-parrainages">
          <p className="text-xs text-[var(--muted)]">
            <Gift size={12} className="inline mr-1.5" style={{ color: "var(--primary)" }} />
            {parrainages.appliques.length} parrainage(s) déduit(s) automatiquement · {argent(parrainages.montant_total)}.
            {parrainages.a_verifier.length > 0 && ` ${parrainages.a_verifier.length} cas à trancher ci-dessous.`}
          </p>
          {/* Les cadeaux se cochent et s'impriment dans Facturation → Parrainages. */}
          <p className="text-xs text-[var(--muted)] mt-2" data-testid="parrainages-renvoi-facturation">
            La liste à imprimer et les cadeaux remis se gèrent dans <strong>Facturation → Parrainages</strong>.
          </p>
        </div>
      )}

      {data.groupes.length === 0 && (
        <div className="bg-white rounded-2xl p-8 border text-center" style={{ borderColor: "var(--border)" }} data-testid="notifications-vide">
          <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center" style={{ background: GRAVITES.vert.fond, color: GRAVITES.vert.texte }}>
            <Check size={22} />
          </div>
          <p className="mt-4 font-semibold">Tout est à jour.</p>
        </div>
      )}

      {data.groupes.map((g) => {
        const c = GRAVITES[g.gravite];
        const ouvert = ouverts[g.id];
        const selection = choisis[g.id] || [];
        const action = (["nouvelles_inscriptions", "reservations_spectacle"].includes(g.id)
          ? ACTIONS.lu
          : ACTIONS[g.type_action]) || ACTIONS.vu;
        const visibles = g.items;
        const tous = visibles.length > 0 && visibles.every((i) => selection.includes(i.ref));
        return (
          <Section
            key={g.id}
            testid={`notif-groupe-${g.id}`}
            titre={
              <span className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.puce }} />
                {g.titre}
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: c.fond, color: c.texte, border: `1px solid ${c.bord}` }}>{g.nb}</span>
              </span>
            }
            aide={g.action}
            action={
              <button className="btn-ghost" data-testid={`notif-toggle-${g.id}`}
                onClick={() => setOuverts({ ...ouverts, [g.id]: !ouvert })}>
                {ouvert ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                {ouvert ? "Replier" : `Voir les ${g.nb}`}
              </button>
            }
          >
            {ouvert && (
              <div className="space-y-3">
                {g.selection && (
                  <div className="flex items-center gap-3 px-1">
                    <Case cochee={tous} onChange={(v) => setChoisis({ ...choisis, [g.id]: v ? visibles.map((i) => i.ref) : [] })}
                      testid={`notif-tout-${g.id}`} titre="Tout sélectionner" />
                    <span className="text-xs text-[var(--muted)]">Tout sélectionner ({visibles.length})</span>
                    <button className="btn-ghost ml-auto" data-testid={`notif-reafficher-${g.id}`}
                      onClick={() => reafficher(g.id)}>
                      <RotateCcw size={14} /> Revoir ce qui a été masqué
                    </button>
                    {g.id === "nouvelles_inscriptions" && (
                      <button className="btn-ghost" data-testid="notif-nouvelles-tout-vu"
                        onClick={async () => {
                          if (!await demander({
                            titre: `Marquer ${g.nb} inscription(s) comme lues`,
                            texte: "La liste repart de zéro. Les prochaines inscriptions arrivées du site réapparaîtront ici toutes seules.",
                            points: ["Rien n'est effacé : les inscriptions restent dans les listes et les fiches élèves."],
                            valider: "Tout marquer comme lu",
                          })) return;
                          await api("/notifications/nouvelles-inscriptions/tout-vu", { method: "POST" });
                          onRecharger();
                        }}>
                        <Check size={14} /> Tout marquer comme lu ({g.nb})
                      </button>
                    )}
                  </div>
                )}

                {selection.length > 0 && (action.lot || g.id === "messages") && (
                  <BarreSelection nb={selection.length} testid={`notif-barre-${g.id}`}>
                    {g.id === "avs_manquants" && (
                      <ApercuEmail
                        chemin="/notifications/avs/apercu?public=enfant"
                        titre="Demande de numéro AVS" />
                    )}
                    {g.id === "messages" ? (
                      <BoutonSombre onClick={() => marquerMessagesTraites(selection)}
                        disabled={enCours === g.id} testid="notif-lot-messages-traite">
                        <Check size={14} /> Marquer la sélection traitée
                      </BoutonSombre>
                    ) : (
                      <BoutonSombre onClick={() => executer(g.id, g.type_action, selection, visibles)}
                        disabled={enCours === g.id} testid={`notif-lot-${g.id}`}>
                        {enCours === g.id ? <Loader2 size={14} className="animate-spin" /> : <action.Icone size={14} />} {libelleLot(g, action, selection, visibles)}
                      </BoutonSombre>
                    )}
                    {g.type_action === "demander_avs" && familles(selection, visibles) > PAQUET_AVS && (
                      <span className="text-xs w-full text-right" style={{ color: "#CBD5E1" }} data-testid="notif-avs-paquets">
                        Envoi par paquets de {PAQUET_AVS} familles : reclique le bouton pour le paquet suivant
                        (la console dit combien il en reste).
                      </span>
                    )}
                  </BarreSelection>
                )}
                {g.id === "parrainages" && parrainages && (
                  <DecisionsPrises decisions={parrainages.decisions} onFait={onRecharger} />
                )}

                <ul className="bg-white rounded-2xl border divide-y overflow-hidden" style={{ borderColor: "var(--border)" }}>
                  {visibles.map((it) => (
                    <li key={it.ref} className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-[var(--primary-tint)] transition-colors"
                      data-testid={`notif-item-${g.id}`}>
                      {g.selection && (
                        <Case
                          cochee={selection.includes(it.ref)}
                          onChange={(v) => setChoisis({
                            ...choisis,
                            [g.id]: v ? [...selection, it.ref] : selection.filter((x) => x !== it.ref),
                          })}
                          testid={`notif-case-${g.id}`}
                        />
                      )}
                      <div className="min-w-[200px]">
                        <div className="font-medium text-sm">{it.titre}</div>
                        <div className="text-xs text-[var(--muted)]">{it.detail}</div>
                      </div>
                      <div className="ml-auto flex items-center gap-2 flex-wrap">
                        {g.id === "parrainages" ? (
                          <DecisionParrainage item={it} onFait={onRecharger} />
                        ) : g.id === "avs_manquants" ? (
                          <>
                            <SaisieAvs eleveId={it.ref} onFait={onRecharger} />
                            <button className="btn-ghost" data-testid={`notif-action-${g.id}`}
                              disabled={enCours === g.id}
                              onClick={() => executer(g.id, g.type_action, [it.ref], visibles)}>
                              <action.Icone size={14} /> {action.label}
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn-ghost" data-testid={`notif-ouvrir-${g.id}`} onClick={() => onAller(it.cible)}>
                              {/* « Corriger l'adresse » (adresses rejetées) : le serveur dit ce que le bouton ouvre. */}
                              <ExternalLink size={14} /> {it.bouton || "Ouvrir"}
                            </button>
                            {g.id === "desinscriptions" && (
                              <DecisionDesinscription item={it} onFait={onRecharger} />
                            )}
                            {g.id === "avs_a_attribuer" && (
                              <AttributionAvs item={it} onFait={onRecharger} />
                            )}
                            {g.id === "messages" && (
                              <button className="btn-ghost" data-testid="notif-messages-traite"
                                disabled={enCours === g.id}
                                onClick={() => marquerMessagesTraites([it.ref])}>
                                <Check size={14} /> Marquer traité
                              </button>
                            )}
                            {(action.lot || action.parLigne) && (
                              <button className="btn-ghost" data-testid={`notif-action-${g.id}`}
                                disabled={enCours === g.id}
                                onClick={() => executer(g.id, g.type_action, [it.ref], visibles)}>
                                <action.Icone size={14} /> {action.label}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>
        );
      })}

      {/* Les groupes qui ont des lignes masquées, y compris ceux ENTIÈREMENT masqués : ils
          ne sont plus affichés ci-dessus (rien à traiter), et leur bouton « Revoir ce qui a
          été masqué » partait avec eux — une ligne masquée par erreur ne revenait plus. */}
      {/* Repliée par défaut (école, 09.10.2026 : « est-ce qu'elle peut être repliée par
          défaut ? ») : ce n'est pas du travail à faire. Le choix de chacun est retenu sur
          son navigateur. */}
      {(data.masques || []).length > 0 && (
        <Section testid="notif-masques"
          titre={<span className="flex items-center gap-2"><Eye size={15} /> Lignes masquées
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ background: "#EEF2F6", color: "#475569", border: "1px solid var(--border)" }}
              data-testid="notif-masques-nb">{data.masques.reduce((s, m) => s + (m.nb || 0), 0)}</span></span>}
          aide="Ce qui a été rangé par « Vu, c'est normal » (ou « Marquer comme lu »). Un groupe entièrement masqué disparaît de la liste : c'est d'ici qu'on le fait revenir."
          action={
            <button className="btn-ghost" data-testid="notif-masques-toggle"
              onClick={() => basculerMasques(!masquesOuverts)}>
              {masquesOuverts ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              {masquesOuverts ? "Replier" : `Afficher (${data.masques.length} groupe${data.masques.length > 1 ? "s" : ""})`}
            </button>
          }>
          {masquesOuverts && (
          <ul className="bg-white rounded-2xl border divide-y overflow-hidden" style={{ borderColor: "var(--border)" }}>
            {data.masques.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`notif-masque-${m.id}`}>
                <div className="min-w-[200px]">
                  <div className="font-medium text-sm">{m.titre}</div>
                  <div className="text-xs text-[var(--muted)]">
                    {m.nb} ligne(s) masquée(s)
                    {m.visibles ? ` · ${m.visibles} encore affichée(s) plus haut` : " · groupe entièrement masqué"}
                  </div>
                </div>
                <button className="btn-ghost ml-auto" data-testid={`notif-masque-reafficher-${m.id}`}
                  onClick={() => reafficher(m.id)}>
                  <RotateCcw size={14} /> Revoir ce qui a été masqué
                </button>
              </li>
            ))}
          </ul>
          )}
        </Section>
      )}
    </div>
  );
}
